<script lang="ts">
  /**
   * MenuSheet — the header's Menu control (spec 2026-09-09 §1e, the Oura row).
   *
   * Five rows, top to bottom, nothing else:
   *   1. Your name — the club MemberName the shell reports (`getClubStatus().myName`),
   *      read-only here; the Club tab owns editing it.
   *   2. Invite to the club — the existing bridge (`openClubSettings`, the native
   *      ClubSettingsView carries "Invite clubmates"); method-gated so a shell
   *      without it, or a plain browser, shows the row as not available instead
   *      of a tap that goes nowhere.
   *   3. Export data (JSON) — today's firehose export, byte-identical
   *      (`shareFirehose`), moved here from the old FAB sheet.
   *   4. Send feedback — the existing wish-list form (`WishlistButton`, posts to
   *      /api/wishlist).
   *   5. About — the product line and which build this page is.
   *
   * Mounted by App.svelte OUTSIDE #app-shell (beside OverlayHost) so it stays
   * interactive while the shell is inert. Same scrim + sheet shape as the other
   * sheets; single-slot overlay store keeps it exclusive with them. Closes on
   * Done (in the head, beside the grabber), the backdrop, Escape, or a drag down
   * (the shared gesture, lane sheet-swipe 2026-09-10).
   */
  import { bridge } from "../bridge/client";
  import { supportsMethodStore } from "../lib/nativeMethods";
  import { currentRawFirehose } from "../lib/state";
  import { shareFirehose } from "../lib/share";
  import { closeOverlay } from "../lib/overlay";
  import { haptic } from "../lib/haptics";
  import { lockBodyScroll, unlockBodyScroll } from "../lib/native/bodyScrollLock";
  import { sheetDrag } from "../lib/sheetDrag";
  import { devClubState, devClubStatus } from "../lib/clubDevState";
  import { BUILD_INFO, webLoadSource } from "../lib/buildInfo";
  import WishlistButton from "./WishlistButton.svelte";

  let { open = false }: { open?: boolean } = $props();

  $effect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  });

  const supportsClubStatus = supportsMethodStore("getClubStatus");
  const supportsClubSettings = supportsMethodStore("openClubSettings");
  // Browser-only `?clubState=…` seam (never on a native shell) — the same one
  // ClubTab uses, so a desktop still can show a named member.
  const devState = devClubState();

  // null = not asked yet / no shell; "" = shell answered with no name.
  let myName = $state<string | null>(devState ? (devClubStatus(devState).myName ?? "") : null);
  let nameLoaded = $state(devState !== null);

  async function loadName(): Promise<void> {
    try {
      const status = await bridge.getClubStatus();
      myName = status.myName ?? "";
    } catch {
      myName = "";
    } finally {
      nameLoaded = true;
    }
  }

  // Ask once per open while the shell can answer; a re-open re-reads so a name
  // set on the Club tab in the meantime shows up.
  $effect(() => {
    if (open && !devState && $supportsClubStatus) void loadName();
  });

  let exportStatus = $state<string | null>(null);
  let exporting = $state(false);
  let feedbackOpen = $state(false);
  let aboutOpen = $state(false);

  // OverlayHost-style mount: the component stays mounted and `open` toggles, so
  // transient state is reset on close rather than surviving into the next open.
  $effect(() => {
    if (!open) {
      exportStatus = null;
      feedbackOpen = false;
      aboutOpen = false;
    }
  });

  function close() {
    closeOverlay("menu");
  }

  function onInvite() {
    haptic("light");
    void bridge.openClubSettings();
    close();
  }

  async function onExport() {
    const fh = $currentRawFirehose;
    if (!fh || exporting) return;
    haptic("light");
    exporting = true;
    exportStatus = null;
    try {
      const result = await shareFirehose(fh);
      if (result.kind === "shared") exportStatus = "Sent.";
      else if (result.kind === "downloaded") exportStatus = "Saved as a file.";
      else if (result.kind === "cancelled") exportStatus = null;
      else exportStatus = `Couldn't export: ${result.message}`;
    } catch {
      exportStatus = "Couldn't export this swim's data. Tap to try again.";
    } finally {
      exporting = false;
    }
  }

  function onKey(e: KeyboardEvent) {
    if (open && e.key === "Escape") close();
  }

  const shortSha = BUILD_INFO.commitSha === "unknown" ? "unknown" : BUILD_INFO.commitSha.slice(0, 7);
  const loadSource = typeof location !== "undefined" ? webLoadSource(location.href) : "unknown";
</script>

<svelte:window onkeydown={onKey} />

{#if open}
  <div
    class="scrim"
    role="presentation"
    onclick={close}
    onkeydown={(e) => e.key === "Enter" && close()}
  ></div>
  <div class="sheet" role="dialog" aria-modal="true" aria-label="Menu" use:sheetDrag={close}>
    <!-- Head: the grabber, and Done at the right (the share sheet's head). The
         menu had no close control of its own. -->
    <div class="head">
      <div class="grabber" aria-hidden="true"></div>
      <button type="button" class="done" onclick={close}>Done</button>
    </div>

    <!-- 1. Your name (read-only; the Club tab edits it) -->
    <div class="row row--name" data-menu-row="name">
      <span class="avatar" aria-hidden="true">
        {#if myName}{myName.slice(0, 1).toUpperCase()}{:else}·{/if}
      </span>
      <span class="body">
        <span class="title">
          {#if myName}{myName}{:else if nameLoaded}No name yet{:else}Swimmer{/if}
        </span>
        <span class="sub">
          {#if myName}Your name in the club{:else if nameLoaded}Add it on the Club tab{:else}Sign in on your iPhone to name yourself{/if}
        </span>
      </span>
    </div>

    <div class="rows">
      <!-- 2. Invite to the club (existing bridge; gated on the shell having it) -->
      <button
        type="button"
        class="row"
        data-menu-row="invite"
        disabled={!$supportsClubSettings}
        onclick={onInvite}
      >
        <span class="icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="9" cy="8" r="3.5"/>
            <path d="M2.5 20a6.5 6.5 0 0 1 13 0"/>
            <path d="M19 8v6M16 11h6"/>
          </svg>
        </span>
        <span class="body">
          <span class="title">Invite to the club</span>
          <span class="sub">
            {#if $supportsClubSettings}Swim with friends, sets side by side{:else}Available in the iPhone app{/if}
          </span>
        </span>
      </button>

      <!-- 3. Export data (today's JSON export, unchanged) -->
      <button
        type="button"
        class="row"
        data-menu-row="export"
        disabled={$currentRawFirehose === null || exporting}
        onclick={onExport}
      >
        <span class="icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 3v12"/>
            <polyline points="7 10 12 15 17 10"/>
            <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/>
          </svg>
        </span>
        <span class="body">
          <span class="title">Export data (JSON)</span>
          <span class="sub">
            {#if $currentRawFirehose === null}No swim loaded yet{:else if exporting}Packaging…{:else}This swim's full Apple Watch data, as a file{/if}
          </span>
        </span>
      </button>
      {#if exportStatus}
        <div class="status" role="status" aria-live="polite">{exportStatus}</div>
      {/if}

      <!-- 4. Send feedback (the existing wish-list API) -->
      <button
        type="button"
        class="row"
        data-menu-row="feedback"
        aria-expanded={feedbackOpen}
        onclick={() => { haptic("light"); feedbackOpen = !feedbackOpen; }}
      >
        <span class="icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 5h16v11H8l-4 4z"/>
          </svg>
        </span>
        <span class="body">
          <span class="title">Send feedback</span>
          <span class="sub">Tell us what you want next</span>
        </span>
      </button>
      {#if feedbackOpen}
        <div class="slot">
          <WishlistButton context="menu-feedback" prompt="What should Swim Set Club do better?" />
        </div>
      {/if}

      <!-- 5. About -->
      <button
        type="button"
        class="row"
        data-menu-row="about"
        aria-expanded={aboutOpen}
        onclick={() => { haptic("light"); aboutOpen = !aboutOpen; }}
      >
        <span class="icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <path d="M12 11v5M12 8h.01"/>
          </svg>
        </span>
        <span class="body">
          <span class="title">About</span>
          <span class="sub">Swim Set Club</span>
        </span>
      </button>
      {#if aboutOpen}
        <div class="slot about">
          <h3 class="about-h">About Swim Set Club</h3>
          <p>Every data point your Apple Watch recorded during a swim, on one timeline, set by set.</p>
          <p class="policy-link"><a href="https://swimsetclub.com/privacy" rel="noopener">Privacy policy</a></p>
          <p class="policy-link"><a href="https://swimsetclub.com/health-privacy" rel="noopener">Health data policy</a></p>
          <p class="fine">Build {shortSha} · {loadSource} · <a href="https://swimsetclub.com" rel="noopener">swimsetclub.com</a></p>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 50;
    animation: scrim-in var(--dur-card) var(--ease-swim);
    /* A pan that starts on the backdrop scrolls nothing (every OverlayHost scrim). */
    touch-action: none;
  }
  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 51;
    background: var(--bg-card);
    border-top: 1px solid var(--divider);
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    padding: var(--space-3) var(--space-4)
      calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
    box-shadow: var(--shadow-floating);
    /* --bg-card is Dawn Glass (42% alpha); the page must not read through a
       sheet's own text, so blur what is behind it (DayPicker's treatment). */
    backdrop-filter: blur(24px) saturate(1.2);
    -webkit-backdrop-filter: blur(24px) saturate(1.2);
    animation: sheet-in var(--dur-card) var(--ease-swim);
    /* The top edge stays below the status bar: the backdrop above a tall menu
       is still there to tap. */
    max-height: calc(100vh - 48px - env(safe-area-inset-top, 0px));
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y; /* NEVER touch-action: none (WKWebView trap) */
    overscroll-behavior: contain;
  }
  /* The share sheet's head: the grabber at top centre, Done at the right. */
  .head {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-height: 44px;
    margin-top: calc(-1 * var(--space-3));
  }
  .grabber {
    position: absolute;
    top: 10px;
    left: 50%;
    width: 36px;
    height: 4px;
    margin-left: -18px;
    border-radius: 999px;
    background: var(--divider-strong);
  }
  /* ScoresDrawer's Done. */
  .done {
    min-height: 44px;
    padding: 0 var(--space-2);
    margin-right: calc(-1 * var(--space-2));
    border: 0;
    background: transparent;
    color: var(--accent-primary);
    font: inherit;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .done:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
  }
  .rows {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    padding: var(--space-3);
    background: var(--bg-surface);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    color: var(--text-primary);
    cursor: pointer;
    text-align: left;
    -webkit-tap-highlight-color: transparent;
    transition: background-color var(--dur-micro) var(--ease-swim),
                opacity var(--dur-micro) var(--ease-swim);
  }
  .row:not(:disabled):hover {
    background: var(--bg-raised);
  }
  .row:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
  }
  .row:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  /* The name row is information, not an action: no border, no hover. */
  .row--name {
    background: transparent;
    border: 0;
    cursor: default;
    padding: var(--space-2) var(--space-1) var(--space-3);
  }
  .avatar {
    display: inline-flex;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    background: var(--accent-primary);
    color: var(--bg-canvas);
    font: var(--type-title-subtitle);
    font-weight: 600;
    flex: 0 0 auto;
  }
  .icon {
    display: inline-flex;
    width: 36px;
    height: 36px;
    border-radius: var(--radius-button);
    align-items: center;
    justify-content: center;
    background: var(--bg-raised);
    color: var(--accent-primary);
    flex: 0 0 auto;
  }
  .body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .title {
    font: var(--type-title-subtitle);
    color: var(--text-primary);
  }
  .sub {
    font: var(--type-body-sm);
    color: var(--text-muted);
  }
  .status {
    padding: var(--space-2) var(--space-3);
    font: var(--type-caption);
    color: var(--accent-primary);
    text-align: center;
    background: var(--bg-surface);
    border-radius: var(--radius-button);
    letter-spacing: 0.04em;
  }
  .slot {
    padding-left: var(--space-3);
  }
  .about-h {
    margin: var(--space-2) 0 var(--space-1);
    font: var(--type-title-subtitle);
    color: var(--text-primary);
  }
  .about p {
    margin: 0 0 var(--space-2);
    font: var(--type-body-sm);
    color: var(--text-body);
  }
  .about .fine {
    font: var(--type-caption);
    color: var(--text-muted);
    letter-spacing: 0.04em;
  }
  .about a {
    color: var(--accent-secondary);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .about .policy-link { margin: 0; }
  .about .policy-link a {
    display: inline-flex;
    align-items: center;
    min-height: 44px; /* the brief's 44pt target */
  }

  @keyframes scrim-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes sheet-in {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
</style>
